import {effect, inject, Injectable, signal, WritableSignal} from '@angular/core';
import {Database, onValue, ref, serverTimestamp, update} from 'firebase/database';
import {FirebaseInitService} from '../shared/firebase-init.service';
import {App} from '@capacitor/app';
import {ProfileService} from './profile.service';

@Injectable()
export class PresenceService {

	currentUid: string = '';
	private readonly firebaseInit = inject(FirebaseInitService);
	private readonly database: Database = this.firebaseInit.database;
	private readonly profileService = inject(ProfileService);
	private readonly presenceObervers: Map<string, WritableSignal<{ status: string } | null>> = new Map<string, WritableSignal<{ status: string } | null>>();
	private appStateListenerAdded = false;

	constructor() {
		effect(() => {
			const profile = this.profileService.profile();
			if (!profile?.uuid) {
				return;
			}
			this.currentUid = profile.uuid;
			this.updateOnUser();
			if (!this.appStateListenerAdded) {
				this.appStateListenerAdded = true;
				App.addListener('appStateChange', ({isActive}) => {
					if (isActive) {
						this.updateOnUser();
					} else {
						this.updateOnAway();
					}
				}).then();
			}
		});
	}

	get timestamp() {
		return serverTimestamp();
	}

	getPresence(uid: string) {
		if (this.presenceObervers.has(uid)) {
			return this.presenceObervers.get(uid)!.asReadonly();
		}
		const presence = signal<{ status: string } | null>(null);
		this.presenceObervers.set(uid, presence);
		onValue(ref(this.database, `status/${uid}`), (snapshot => presence.set(snapshot.val())));
		return presence.asReadonly();
	}

	setPresence(status: string) {
		return update(ref(this.database, `status/${this.currentUid}`), {
			status,
			timestamp: this.timestamp,
			uid: this.currentUid
		});
	}

	// Updates status when logged-in connection to Firebase starts
	updateOnUser() {
		onValue(ref(this.database, '.info/connected'), (snapshot: { val: () => boolean }) => this.setPresence(snapshot.val() ? 'online' : 'offline'));
	}

	signOut() {
		return this.setPresence('offline');
	}

	updateOnAway() {
		this.setPresence((document.visibilityState === 'hidden') ? 'away' : 'online').then();
	}
}
